/**
 * Alias Management Page (Admin)
 * Manage commodity alias mappings (FR-603)
 */

import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  InputAdornment,
  Alert,
  Autocomplete
} from '@mui/material';
import { Icon } from '@iconify/react';
import { visuallyHidden } from '@mui/utils';
import { PageHeader, LoadingSkeleton, EmptyState, ConfirmDialog } from '../../components/common';
import { useQueryClient } from '@tanstack/react-query';
import { useAliases, useCommodities } from '../../services/queries';
import { createAlias, updateAlias, deleteAlias } from '../../services/admin';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';

// Monospace font for numeric/ID values
const firaCode = { fontFamily: MONO };

const BREADCRUMBS = [
  { label: 'Home', path: '/' },
  { label: 'Admin', path: '/admin' },
  { label: 'Alias Management' }
];

function AliasManagement() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();
  const { data: commodityAliases = [], isLoading: l1 } = useAliases();
  const { data: commodities = [], isLoading: l2 } = useCommodities();
  const loading = l1 || l2;
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirmAlias, setConfirmAlias] = useState(null);
  const [newAlias, setNewAlias] = useState('');
  const [selectedCommodity, setSelectedCommodity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const ctaSx = { ...recipes.primaryBtnSx, px: 2.5, py: 0.9, fontSize: '0.875rem', textTransform: 'none' };

  // Filter aliases by search
  const filteredAliases = commodityAliases.filter(a => {
    const commodity = commodities.find(c => c.id === a.commodityId);
    return (
      a.alias.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commodity?.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Group aliases by commodity
  const aliasesByCommodity = commodities.reduce((acc, commodity) => {
    const aliases = commodityAliases.filter(a => a.commodityId === commodity.id);
    if (aliases.length > 0) {
      acc[commodity.id] = {
        commodity,
        aliases
      };
    }
    return acc;
  }, {});

  const handleAdd = () => {
    setEditItem(null);
    setNewAlias('');
    setSelectedCommodity(null);
    setDialogOpen(true);
  };

  const handleEdit = (alias) => {
    setEditItem(alias);
    setNewAlias(alias.alias);
    setSelectedCommodity(commodities.find(c => c.id === alias.commodityId) || null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditItem(null);
    setNewAlias('');
    setSelectedCommodity(null);
  };

  // An alias string must resolve to exactly one commodity: the upload matcher
  // takes the first mapping it finds, so a duplicate would silently decide
  // which commodity a spreadsheet row lands on.
  const duplicateAlias = React.useMemo(() => {
    const candidate = newAlias.trim().toLowerCase();
    if (!candidate) return null;
    return (
      commodityAliases.find(
        (a) => a.alias.trim().toLowerCase() === candidate && a.id !== editItem?.id
      ) || null
    );
  }, [newAlias, commodityAliases, editItem]);

  const handleSave = async () => {
    if (!newAlias || !selectedCommodity || duplicateAlias) return;
    setSaving(true);
    setError('');
    try {
      if (editItem) {
        await updateAlias(editItem.id, { alias: newAlias, commodityId: selectedCommodity.id });
      } else {
        await createAlias({ alias: newAlias, commodityId: selectedCommodity.id });
      }
      queryClient.invalidateQueries({ queryKey: ['aliases'] });
      handleDialogClose();
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (alias) => {
    setError('');
    try {
      await deleteAlias(alias.id);
      queryClient.invalidateQueries({ queryKey: ['aliases'] });
    } catch (e) {
      setError(e?.message || 'Delete failed.');
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader
          eyebrow="Admin"
          title="Alias Management"
          subtitle="Map variant names to canonical commodity definitions"
          breadcrumbs={BREADCRUMBS}
        />
        <LoadingSkeleton variant="page" />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Admin"
        title="Alias Management"
        subtitle="Map variant names to canonical commodity definitions"
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button
            variant="contained"
            disableElevation
            startIcon={<Icon icon="ph:plus" width={16} height={16} />}
            onClick={handleAdd}
            sx={ctaSx}
          >
            Add Alias
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Info note */}
      <Alert severity="info" sx={{ mb: 3, bgcolor: palette.infoBg, border: `1px solid ${palette.info}30`, color: palette.text, '& .MuiAlert-icon': { color: palette.info } }}>
        Commodity aliases help normalize variant names during data upload.
        When price data contains an alias name, it will be automatically mapped to the canonical commodity.
      </Alert>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search aliases or commodity names"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            sx={recipes.searchSx}
            inputProps={{ 'aria-label': 'Search aliases or commodity names' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="ph:magnifying-glass" width={17} height={17} color={palette.textMuted} />
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Alias Table */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardHeader
              title="All Aliases"
              titleTypographyProps={{ variant: 'h6' }}
              subheader={`${filteredAliases.length} alias${filteredAliases.length === 1 ? '' : 'es'} configured`}
            />
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={recipes.headCellSx}>ID</TableCell>
                      <TableCell sx={recipes.headCellSx}>Alias Name</TableCell>
                      <TableCell sx={recipes.headCellSx}>
                        <Box component="span" sx={visuallyHidden}>Maps to</Box>
                      </TableCell>
                      <TableCell sx={recipes.headCellSx}>Maps To (Canonical)</TableCell>
                      <TableCell sx={recipes.headCellSx}>Status</TableCell>
                      <TableCell align="center" sx={recipes.headCellSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAliases
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((alias) => {
                      const commodity = commodities.find(c => c.id === alias.commodityId);
                      return (
                        <TableRow key={alias.id} hover sx={recipes.bodyRowSx}>
                          <TableCell>
                            <Typography variant="body2" sx={{ ...firaCode, color: palette.textMuted, fontSize: '0.78rem' }}>
                              {alias.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<Icon icon="ph:swap" width={14} height={14} />}
                              label={alias.alias}
                              variant="outlined"
                              sx={{ borderColor: palette.cardBorder, color: palette.text, fontWeight: 600, '& .MuiChip-icon': { color: palette.textSecondary } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Icon icon="ph:arrow-right" width={16} height={16} color={palette.textMuted} aria-hidden="true" />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {commodity?.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                                {commodity?.canonicalName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={alias.status.toUpperCase()}
                              sx={{
                                height: 20, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                                bgcolor: alias.status === 'active' ? palette.accentBg : palette.surfaceHover,
                                color: alias.status === 'active' ? palette.accent : palette.textMuted
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Edit">
                              <IconButton size="small" aria-label={`Edit alias ${alias.alias}`} onClick={() => handleEdit(alias)} sx={{ color: palette.textMuted }}>
                                <Icon icon="ph:pencil-simple" width={16} height={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" aria-label={`Delete alias ${alias.alias}`} onClick={() => setConfirmAlias(alias)} sx={{ color: palette.danger }}>
                                <Icon icon="ph:trash" width={16} height={16} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredAliases.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ border: 0, p: 2.5 }}>
                          <EmptyState
                            compact
                            variant="dashed"
                            icon={<Icon icon="ph:swap" width={24} height={24} />}
                            title="No matching aliases"
                            description="No aliases match the current search."
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredAliases.length > 0 && (
                <Box sx={{ ...recipes.tableFooterSx, display: 'flex', justifyContent: 'flex-end', py: 0 }}>
                  <TablePagination
                    component="div"
                    count={filteredAliases.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    sx={recipes.paginationSx}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Aliases Grouped by Commodity */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader
              title="Aliases by Commodity"
              titleTypographyProps={{ variant: 'h6' }}
              subheader="Click a chip to edit, or its × to delete"
            />
            <CardContent sx={{ maxHeight: 500, overflow: 'auto' }}>
              {Object.values(aliasesByCommodity).map(({ commodity, aliases }) => (
                <Box key={commodity.id} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {commodity.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {aliases.map(alias => (
                      <Chip
                        key={alias.id}
                        label={alias.alias}
                        size="small"
                        variant="outlined"
                        onClick={() => handleEdit(alias)}
                        onDelete={() => setConfirmAlias(alias)}
                        sx={{
                          borderColor: palette.cardBorder,
                          color: palette.text,
                          '&:hover': { bgcolor: palette.surfaceHover },
                          '& .MuiChip-deleteIcon': { color: palette.textMuted, '&:hover': { color: palette.danger } }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title="Statistics"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: palette.textSecondary }}>Total Aliases</Typography>
                  <Typography variant="body2" fontWeight={600} sx={firaCode}>{commodityAliases.length}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: palette.textSecondary }}>Active</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ ...firaCode, color: palette.accent }}>
                    {commodityAliases.filter(a => a.status === 'active').length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: palette.textSecondary }}>Commodities with Aliases</Typography>
                  <Typography variant="body2" fontWeight={600} sx={firaCode}>
                    {Object.keys(aliasesByCommodity).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 520 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editItem ? 'Edit Alias' : 'Add New Alias'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Alias Name"
              fullWidth
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              helperText="The variant name that appears in uploaded data"
              sx={recipes.searchSx}
              InputLabelProps={{ sx: recipes.labelSx }}
            />

            <Autocomplete
              options={commodities}
              getOptionLabel={(option) => `${option.name} (${option.canonicalName})`}
              value={selectedCommodity}
              onChange={(e, newValue) => setSelectedCommodity(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Maps To Commodity"
                  helperText="The canonical commodity this alias represents"
                  sx={recipes.searchSx}
                  InputLabelProps={{ ...params.InputLabelProps, sx: recipes.labelSx }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" sx={{ color: palette.textMuted }}>
                      {option.canonicalName}
                    </Typography>
                  </Box>
                </li>
              )}
            />

            {duplicateAlias ? (
              <Alert severity="error">
                <Typography variant="body2">
                  <strong>"{duplicateAlias.alias}"</strong> already maps to{' '}
                  <strong>
                    {commodities.find((c) => c.id === duplicateAlias.commodityId)?.name || 'another commodity'}
                  </strong>
                  . Each alias can point to only one commodity, so edit that entry instead of adding a second.
                </Typography>
              </Alert>
            ) : (
              newAlias && selectedCommodity && (
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>"{newAlias}"</strong> will be mapped to <strong>{selectedCommodity.name}</strong>
                  </Typography>
                </Alert>
              )
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={{ color: palette.textMuted, textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={!newAlias || !selectedCommodity || saving || !!duplicateAlias}
            sx={ctaSx}
          >
            {saving ? 'Saving…' : editItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!confirmAlias}
        onClose={() => setConfirmAlias(null)}
        onConfirm={() => { handleDelete(confirmAlias); setConfirmAlias(null); }}
        title="Delete this alias?"
        description={
          confirmAlias
            ? `"${confirmAlias.alias}" will no longer map to ${commodities.find(c => c.id === confirmAlias.commodityId)?.name || 'its commodity'} during data upload.`
            : 'This alias mapping will be removed.'
        }
        confirmLabel="Delete alias"
        tone="danger"
        icon="ph:trash"
      />
    </Box>
  );
}

export default AliasManagement;
